// Centralised mapping of Command Centre actions to routes and tab membership.
// Used by flowchart panels, graduated tab logic, and usage tracking.

export interface NavAction {
  label: string
  route: string | null // null = unmapped / not yet available
  tab: string
}

// Every action available in the Command Centre flowchart panels
export const NAV_ACTIONS: Record<string, NavAction> = {
  // ── Accounts tab ──
  'accounts-list':        { label: 'Accounts List',          route: '/accounts',            tab: 'accounts' },
  'journal-entry':        { label: 'Record Journal Entry',   route: '/journal-entries/new',  tab: 'accounts' },
  'transaction-journal':  { label: 'Transaction Journal',    route: '/journal-entries',      tab: 'accounts' },
  'send-to-accountant':   { label: 'Send to Accountant',     route: null,                   tab: 'accounts' },
  'transfer-money':       { label: 'Transfer Money',         route: '/journal-entries/new',  tab: 'accounts' },
  'company-data-auditor': { label: 'Company Data Auditor',   route: null,                   tab: 'accounts' },
  'gst-provisional-tax':  { label: 'GST & Provisional Tax',  route: null,                   tab: 'accounts' },
  'profit-loss':          { label: 'Profit & Loss',          route: '/reports',              tab: 'accounts' },
  'balance-sheet':        { label: 'Balance Sheet',          route: '/reports',              tab: 'accounts' },
  'trial-balance':        { label: 'Trial Balance',          route: '/reports',              tab: 'accounts' },
  'general-ledger':       { label: 'General Ledger',         route: '/reports',              tab: 'accounts' },

  // ── Banking tab ──
  'bank-accounts':        { label: 'Bank Accounts',          route: '/banking',              tab: 'banking' },
  'bank-register':        { label: 'Bank Register',          route: '/banking',              tab: 'banking' },
  'reconcile-accounts':   { label: 'Reconcile Accounts',     route: '/banking',              tab: 'banking' },
  'bank-statement':       { label: 'Bank Statement Import',  route: '/journal-entries',      tab: 'banking' },
  'bank-feeds':           { label: 'Bank Feeds',             route: '/banking',              tab: 'banking' },
  'spend-money':          { label: 'Spend Money',            route: '/journal-entries/new',  tab: 'banking' },
  'receive-money':        { label: 'Receive Money',          route: '/journal-entries/new',  tab: 'banking' },
  'in-tray-banking':      { label: 'In Tray',                route: null,                    tab: 'banking' },
  'prepare-bas':          { label: 'Prepare BAS/IAS',        route: null,                    tab: 'banking' },
  'print-cheques':        { label: 'Print Cheques',          route: null,                    tab: 'banking' },
  'prepare-electronic':   { label: 'Prepare Electronic Payments', route: null,               tab: 'banking' },
  'print-receipts-bank':  { label: 'Print Receipts',         route: null,                    tab: 'banking' },
  'send-remittance-bank': { label: 'Send Remittance Advices', route: null,                   tab: 'banking' },
  'prepare-bank-deposit': { label: 'Prepare Bank Deposit',   route: null,                    tab: 'banking' },
  'transaction-journal-bank': { label: 'Transaction Journal', route: '/journal-entries',     tab: 'banking' },

  // ── Sales tab ──
  'quotes':               { label: 'Quotes',                 route: '/quotes',               tab: 'sales' },
  'invoices':             { label: 'Invoices',               route: '/invoices',             tab: 'sales' },
  'credit-notes':         { label: 'Credit Notes',           route: '/credit-notes',         tab: 'sales' },
  'sales-register':       { label: 'Sales Register',         route: '/invoices',             tab: 'sales' },
  'enter-sales':          { label: 'Enter Sales',            route: '/invoices/new',         tab: 'sales' },
  'receive-payments':     { label: 'Receive Payments',       route: '/journal-entries/new',  tab: 'sales' },
  'online-invoice-payments': { label: 'Online Invoice Payments', route: null,                tab: 'sales' },
  'print-invoices':       { label: 'Print/Email Invoices',   route: null,                    tab: 'sales' },
  'print-statements':     { label: 'Print/Email Statements', route: null,                    tab: 'sales' },
  'invoice-reminders':    { label: 'Invoice Reminders',      route: null,                    tab: 'sales' },
  'print-receipts-sales': { label: 'Print Receipts',         route: null,                    tab: 'sales' },
  'customer-list':        { label: 'Customer List',          route: '/contacts',             tab: 'sales' },
  'transaction-journal-sales': { label: 'Transaction Journal', route: '/journal-entries',    tab: 'sales' },

  // ── Time Billing tab ──
  'time-entries':         { label: 'Time Entries',           route: '/time-tracking',        tab: 'time-billing' },
  'jobs':                 { label: 'Jobs',                   route: '/jobs',                 tab: 'time-billing' },
  'activities-list':      { label: 'Activities List',        route: null,                    tab: 'time-billing' },
  'activity-slips':       { label: 'Activity Slips',         route: '/time-tracking',        tab: 'time-billing' },
  'enter-activity-slip':  { label: 'Enter Activity Slip',    route: null,                    tab: 'time-billing' },
  'activity-log':         { label: 'Activity Log',           route: null,                    tab: 'time-billing' },
  'prepare-time-billing': { label: 'Prepare Time Billing Invoice', route: null,              tab: 'time-billing' },
  'transaction-journal-time': { label: 'Transaction Journal', route: '/journal-entries',     tab: 'time-billing' },
  'timer':                { label: 'Timer',                  route: null,                    tab: 'time-billing' },

  // ── Purchases tab ──
  'purchase-orders':      { label: 'Purchase Orders',        route: '/purchase-orders',      tab: 'purchases' },
  'enter-purchases':      { label: 'Enter Purchases',        route: '/purchase-orders',      tab: 'purchases' },
  'purchase-register':    { label: 'Purchases Register',     route: '/purchase-orders',      tab: 'purchases' },
  'pay-bills':            { label: 'Pay Bills',              route: '/journal-entries/new',  tab: 'purchases' },
  'in-tray-purchases':    { label: 'In Tray',                route: null,                    tab: 'purchases' },
  'print-purchase-orders': { label: 'Print/Email Purchase Orders', route: null,              tab: 'purchases' },
  'print-remittance':     { label: 'Print / Email Remittance Advices', route: null,          tab: 'purchases' },
  'prepare-electronic-purchases': { label: 'Prepare Electronic Payments', route: null,       tab: 'purchases' },
  'print-cheques-purchases': { label: 'Print Cheques',       route: null,                    tab: 'purchases' },
  'send-remittance-purchases': { label: 'Send Remittance Advices', route: null,              tab: 'purchases' },
  'expense-claims':       { label: 'Expense Claims',         route: '/expense-claims',       tab: 'purchases' },
  'supplier-list':        { label: 'Supplier List',          route: '/contacts',             tab: 'purchases' },
  'transaction-journal-purchases': { label: 'Transaction Journal', route: '/journal-entries', tab: 'purchases' },

  // ── Inventory tab ──
  'items-list':           { label: 'Items List',             route: '/inventory',            tab: 'inventory' },
  'items-register':       { label: 'Items Register',         route: '/inventory',            tab: 'inventory' },
  'fixed-assets':         { label: 'Fixed Assets',           route: '/fixed-assets',         tab: 'inventory' },
  'count-inventory':      { label: 'Count Inventory',        route: null,                    tab: 'inventory' },
  'adjust-inventory':     { label: 'Adjust Inventory',       route: null,                    tab: 'inventory' },
  'set-item-prices':      { label: 'Set Item Prices',        route: null,                    tab: 'inventory' },
  'receive-items':        { label: 'Receive Items',          route: null,                    tab: 'inventory' },
  'build-items':          { label: 'Build Items',            route: null,                    tab: 'inventory' },
  'auto-build-items':     { label: 'Auto-Build Items',       route: null,                    tab: 'inventory' },
  'locations':            { label: 'Locations',              route: null,                    tab: 'inventory' },
  'move-items':           { label: 'Move Items',             route: null,                    tab: 'inventory' },
  'in-tray':              { label: 'In Tray',                route: null,                    tab: 'inventory' },
  'transaction-journal-inventory': { label: 'Transaction Journal', route: '/journal-entries', tab: 'inventory' },

  // ── Card File tab ──
  'contacts':             { label: 'Contacts',               route: '/contacts',             tab: 'card-file' },
  'cards-list':           { label: 'Cards List',             route: '/contacts',             tab: 'card-file' },
  'contact-log':          { label: 'Contact Log',            route: null,                    tab: 'card-file' },
  'jobs-list':            { label: 'Jobs',                   route: '/jobs',                 tab: 'card-file' },
  'categories':           { label: 'Categories',             route: null,                    tab: 'card-file' },
  'print-mailing-labels': { label: 'Print Mailing Labels',   route: null,                    tab: 'card-file' },
  'create-personalised-letters': { label: 'Create Personalised Letters', route: null,         tab: 'card-file' },
}

// Tab routes for graduated tab navigation (from CommandCentreLayout)
export const TAB_ROUTES: Record<string, string> = {
  accounts: '/accounts',
  banking: '/banking',
  sales: '/invoices',
  'time-billing': '/time-tracking',
  purchases: '/purchase-orders',
  inventory: '/inventory',
  'card-file': '/contacts',
}

// Actions grouped by tab - useful for rendering panels
export function actionsForTab(tabId: string): NavAction[] {
  return Object.values(NAV_ACTIONS).filter(a => a.tab === tabId)
}

// All tabs that contain at least one mapped (non-null) route
export const TABS_WITH_ROUTES = [...new Set(
  Object.values(NAV_ACTIONS)
    .filter(a => a.route !== null)
    .map(a => a.tab)
)]

// All routes reachable from the Command Centre flowchart (deduped)
export const CC_ROUTES: Set<string> = new Set(
  Object.values(NAV_ACTIONS)
    .map(a => a.route)
    .filter((r): r is string => r !== null)
)

// Check whether a pathname is inside a CC-mapped route (prefix match)
export function isInsideCCRoute(pathname: string): boolean {
  const base = pathname.split('?')[0]
  for (const ccRoute of CC_ROUTES) {
    if (ccRoute === '/') continue
    if (base === ccRoute || base.startsWith(ccRoute + '/')) return true
  }
  return false
}
